'use client'

import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useState,
  useEffect
} from 'react'
import { AccessTokenResponse, getAuthUrl } from '../oauth'
import { getShopLocale } from '@helpers/env'

import {
  ACCOUNT_ACCESS_TOKENS,
  ACCOUNT_ACCESS_TOKENS_EU,
  ACCOUNT_ACCESS_TOKENS_US
} from '../consts'
import trackError from '@helpers/trackError'
import { clearOpenAiCustomer } from '@helpers/openaiAds/pixel'
import { removeCookie } from '@helpers/session'

interface LoginContextType {
  token: string | null
  refreshToken: string | null
  tokenExpiry: number | null
  idToken: string | null
  isLoggedIn: boolean
  login: () => Promise<void>
  resetCredentials: () => void
  storeTokens: (tokens: AccessTokenResponse) => void
}

interface AuthTokens {
  accessToken: string
  refreshToken: string
  idToken: string | null
  tokenExpiry: number
}

const LoginContext = createContext<LoginContextType | undefined>(undefined)

interface LoginProviderProps {
  children: ReactNode
}

export const getTokensStorageLocation = () => {
  switch (getShopLocale()) {
    case 'us':
      return ACCOUNT_ACCESS_TOKENS_US
    case 'eu':
      return ACCOUNT_ACCESS_TOKENS_EU
    default:
      return ACCOUNT_ACCESS_TOKENS
  }
}

export function LoginProvider({ children }: LoginProviderProps) {
  const [token, setToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null)
  const [idToken, setIdToken] = useState<string | null>(null)

  const resetCredentials = useCallback(() => {
    clearOpenAiCustomer()
    removeCookie('customerId')
    console.log('Resetting credentials', new Date().toISOString())
    localStorage.removeItem(getTokensStorageLocation())
    setToken(null)
    setRefreshToken(null)
    setTokenExpiry(null)
    setIdToken(null)
  }, [])

  const storeTokens = (tokens: AccessTokenResponse) => {
    const { access_token, expires_in, id_token, refresh_token } = tokens

    const tokenExpiry = Date.now() + expires_in * 1000

    // We only get idToken on initial token exchange (not on refresh) so we keep track of latest ...
    let latestIdToken = idToken

    // ... and only use id_token if it was received in response
    if (id_token) {
      latestIdToken = id_token
    }

    const authTokens: AuthTokens = {
      accessToken: access_token,
      refreshToken: refresh_token,
      idToken: latestIdToken,
      tokenExpiry
    }

    localStorage.setItem(getTokensStorageLocation(), JSON.stringify(authTokens))

    setToken(access_token)
    setRefreshToken(refresh_token)
    setTokenExpiry(tokenExpiry)
    setIdToken(latestIdToken)
  }

  useEffect(() => {
    const accessTokens = localStorage.getItem(getTokensStorageLocation())

    if (accessTokens) {
      const tokens = JSON.parse(accessTokens) as AuthTokens
      setToken(tokens.accessToken)
      setRefreshToken(tokens.refreshToken)
      setTokenExpiry(tokens.tokenExpiry)
      setIdToken(tokens.idToken)
    }
  }, [])

  const login = async () => {
    const accessToken = localStorage.getItem(getTokensStorageLocation())

    if (accessToken) {
      trackError(new Error('Calling login when access token already exists'), {
        section: 'Account Page'
      })
    }

    const authUrl = await getAuthUrl()

    if (authUrl) {
      window.location.href = authUrl
    }
  }

  return (
    <LoginContext.Provider
      value={{
        token,
        refreshToken,
        tokenExpiry,
        idToken,
        isLoggedIn: !!token,
        login,
        resetCredentials,
        storeTokens
      }}
    >
      {children}
    </LoginContext.Provider>
  )
}

export function useLogin(): LoginContextType {
  const context = useContext(LoginContext)
  if (context === undefined) {
    throw new Error('useLogin must be used within a LoginProvider')
  }
  return context
}
